import { AlarmClock, Check, Languages, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { learnerPaths } from '@app/routes/route-paths'
import { useTranslation } from 'react-i18next'
import { getDashboardLocaleKey, type DashboardWeekday } from '@features/dashboard'
import { useDashboardState } from '@features/dashboard'
import {
  getDisplayMinutesFromSeconds,
  getDisplayMinutesFromSecondsMinOne,
  getLearningActivityMetrics,
  getWeeklyStudiedSecondsForWeekday,
} from './shared/profile-helpers'
import { LmsButton } from '@shared/ui'
import { ProfileAccountBanner } from './components/ProfileAccountBanner'
import { ProfileLegacySurface } from './components/ProfileLegacySurface'
import './profile-layout.css'

function normalizeProfileLocale(locale: string) {
  return getDashboardLocaleKey(locale)
}

function getWeekdayLabel(weekday: DashboardWeekday, locale: string) {
  const localeKey = normalizeProfileLocale(locale)

  return weekday.name[localeKey] ?? weekday.name.en ?? weekday.value
}

function ProfileGoalDay({
  isFinished,
  isSelected,
  label,
}: {
  readonly isFinished: boolean
  readonly isSelected: boolean
  readonly label: string
}) {
  return (
    <div className="profileGoalDay">
      <div
        aria-label={label}
        className="profileGoalIcon"
        data-profile-goal-day="true"
        data-profile-goal-finished={isFinished ? 'true' : 'false'}
        data-profile-goal-selected={isSelected ? 'true' : 'false'}
      >
        {isSelected ? (
          <Check aria-hidden="true" size={22} strokeWidth={2.8} />
        ) : (
          <X aria-hidden="true" size={22} strokeWidth={2.6} />
        )}
      </div>
      <p className="profileGoalLabel">{label}</p>
    </div>
  )
}

export function ProfileScreen() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('translation')
  const { actions, state } = useDashboardState()

  if (!state.myUser) {
    return null
  }

  const selectedGoalDays = state.myUser.my_daily_goal ?? []
  const showGoalCallToAction = selectedGoalDays.length === 0
  const activityMetrics = getLearningActivityMetrics(state.myUser)
  const todayMinutes = getDisplayMinutesFromSecondsMinOne(activityMetrics.dailyStudiedSeconds)
  const todayTargetMinutes = getDisplayMinutesFromSeconds(activityMetrics.todayTargetSeconds)
  const hasGoalToday = activityMetrics.todayTargetSeconds > 0
  const handleOpenStudyPlanSettings = () => {
    actions.setSettingsTab('study-plan')
    navigate(learnerPaths.settings)
  }

  return (
    <div className="profileStack">
      <div className="profileCenteredNarrow">
        <ProfileAccountBanner profile={state.myUser} />
      </div>

      <div className="profileCenteredWide">
        <ProfileLegacySurface padded={false}>
          <div className="profileDailyGoalLayout">
            <div className="profileDailyGoalMain">
              <div className="profileCardStack">
                <div className="profileSectionStack">
                  <h2 className="profileTitle">
                    {t('Daily goal', { defaultValue: 'Daily goal' })}
                  </h2>
                  {hasGoalToday ? (
                    <p className="profileMutedText">
                      {t('minutes', {
                        defaultValue: `${todayMinutes} / ${todayTargetMinutes} minutes`,
                        value: `${todayMinutes} / ${todayTargetMinutes}`,
                      })}
                    </p>
                  ) : (
                    <>
                      <p className="profileMutedText">
                        {t('No goal scheduled today', {
                          defaultValue: 'No goal scheduled today',
                        })}
                      </p>
                      {todayMinutes > 0 ? (
                        <p className="profileMutedText">
                          {t('Studied today', {
                            defaultValue: `Studied today: ${todayMinutes} minutes`,
                            value: todayMinutes,
                          })}
                        </p>
                      ) : null}
                    </>
                  )}
                </div>

                <div className="profileGoalDaysScroller">
                  {state.inWeek.map((weekday) => {
                    const isSelected = selectedGoalDays.includes(weekday.value)
                    const weeklyMinutes = getDisplayMinutesFromSeconds(
                      getWeeklyStudiedSecondsForWeekday(state.myUser, weekday.value),
                    )

                    return (
                      <ProfileGoalDay
                        isFinished={weeklyMinutes >= 15}
                        isSelected={isSelected}
                        key={weekday.value}
                        label={getWeekdayLabel(weekday, i18n.language)}
                      />
                    )
                  })}
                </div>

                <div className="profileTextCenter">
                  <LmsButton
                    className="min-h-11 px-6"
                    onClick={handleOpenStudyPlanSettings}
                    variant={showGoalCallToAction ? 'primary' : 'outline'}
                  >
                    {showGoalCallToAction
                      ? t('Establish learning goals', {
                          defaultValue: 'Establish learning goals',
                        })
                      : t('Edit your Study Plan', {
                          defaultValue: 'Edit your Study Plan',
                        })}
                  </LmsButton>
                </div>
              </div>
            </div>

            <aside className="profileActiveDaysPanel">
              <div className="profileActiveDaysContent">
                <div className="profileInlineRow">
                  <AlarmClock
                    aria-hidden="true"
                    color="var(--lms-color-ink-strong)"
                    size={38}
                    strokeWidth={2.3}
                  />
                  <h2 className="profileTitle">
                    {t('Active Days', { defaultValue: 'Active Days' })}
                  </h2>
                </div>
                <p className="profileActiveDaysValue">
                  {state.myUser.count_daily_activities ?? 0}
                </p>
                <p className="profileActiveDaysHint">
                  {t('Active days description', {
                    defaultValue:
                      'Total days with recorded learning since your account was created. Each day counts once, no matter how many sessions you studied.',
                  })}
                </p>
              </div>
            </aside>
          </div>
        </ProfileLegacySurface>
      </div>

      {!state.myUser.language_default ? (
        <div className="profileCenteredWide">
          <ProfileLegacySurface padded={false}>
            <div className="profileLanguagePrompt">
              <div className="profileInlineRow">
                <Languages
                  aria-hidden="true"
                  color="var(--lms-color-ink-strong)"
                  size={22}
                  strokeWidth={2.3}
                />
                <div>
                  <p className="profileStrongText">
                    {t('Select your language', {
                      defaultValue: 'Select your language',
                    })}
                  </p>
                  <p className="profileMutedText">
                    {t('Choose your display language from the account shell.', {
                      defaultValue:
                        'Choose your display language from the account shell.',
                    })}
                  </p>
                </div>
              </div>
              <LmsButton
                className="min-h-11 px-5"
                onClick={() => {
                  actions.setShowLanguage(true)
                }}
                variant="outline"
              >
                {t('Open selector', { defaultValue: 'Open selector' })}
              </LmsButton>
            </div>
          </ProfileLegacySurface>
        </div>
      ) : null}
    </div>
  )
}
